import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Figma: "Sign up btn - apple" / "Sign up btn - google"
 * - 54px tall, rounded-[26px] pill
 * - text: DM Sans Medium 19/24
 * Variants:
 *   apple: "black" (default, dark fill) | "white" | "white-stroke"
 *   google: "white-stroke" (default) | "white" | "black"
 */

const baseClasses =
  "inline-flex h-[54px] items-center justify-center gap-[10px] rounded-[26px] px-4 py-2.5 text-b1 transition-[colors,transform] duration-200 ease-out motion-safe:md:hover:scale-[1.03] motion-safe:md:active:scale-[0.99] disabled:pointer-events-none disabled:opacity-50";

const AppleLogo = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 15 19" fill="currentColor" aria-hidden className={cn("h-[19px] w-[15px] shrink-0", className)}>
    <path d="M12.4 10.1c0-2.2 1.8-3.3 1.9-3.3-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.7.8-3.3.8-.7 0-1.7-.8-2.9-.8-1.5 0-2.9.9-3.7 2.2-1.6 2.7-.4 6.8 1.1 9 .7 1.1 1.6 2.3 2.8 2.3 1.1 0 1.6-.7 2.9-.7 1.4 0 1.7.7 2.9.7 1.2 0 2-1.1 2.7-2.2.9-1.3 1.2-2.5 1.3-2.6-.1-.1-2.5-1-2.5-3.7zM10.3 3.5c.6-.7 1-1.7.9-2.7-.9 0-1.9.6-2.5 1.3-.6.6-1.1 1.6-1 2.6 1 0 1.9-.5 2.6-1.2z" />
  </svg>
);

const GoogleLogo = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 20 20" aria-hidden className={cn("h-5 w-5 shrink-0", className)}>
    <path fill="#4285F4" d="M19.6 10.2c0-.7-.1-1.4-.2-2H10v3.8h5.4c-.2 1.2-.9 2.3-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3z" />
    <path fill="#34A853" d="M10 20c2.7 0 5-.9 6.6-2.4l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.8-5.6-4.1H1.1v2.6C2.7 17.7 6.1 20 10 20z" />
    <path fill="#FBBC04" d="M4.4 12c-.2-.6-.3-1.3-.3-2s.1-1.4.3-2V5.4H1.1A10 10 0 000 10c0 1.6.4 3.2 1.1 4.6L4.4 12z" />
    <path fill="#EA4335" d="M10 4c1.5 0 2.8.5 3.8 1.5L16.7 2.6C15 1 12.7 0 10 0 6.1 0 2.7 2.3 1.1 5.4L4.4 8C5.2 5.7 7.4 4 10 4z" />
  </svg>
);

type AppleVariant = "black" | "white" | "white-stroke";
type GoogleVariant = "white-stroke" | "white" | "black";

interface AppleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: AppleVariant;
}

export const AppleSignUpButton = React.forwardRef<HTMLButtonElement, AppleButtonProps>(
  ({ className, variant = "black", children, ...props }, ref) => {
    const styles =
      variant === "white-stroke"
        ? "bg-[var(--background-main)] text-text-primary border-2 border-primary-600"
        : variant === "white"
          ? "bg-[var(--background-main)] text-text-primary"
          : "bg-primary-600 text-[var(--background-main)] hover:bg-primary-700";
    return (
      <button ref={ref} className={cn(baseClasses, styles, className)} {...props}>
        <AppleLogo />
        <span>{children ?? "Sign up with Apple"}</span>
      </button>
    );
  },
);
AppleSignUpButton.displayName = "AppleSignUpButton";

interface GoogleButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: GoogleVariant;
}

export const GoogleSignUpButton = React.forwardRef<HTMLButtonElement, GoogleButtonProps>(
  ({ className, variant = "white-stroke", children, ...props }, ref) => {
    const styles =
      variant === "black"
        ? "bg-primary-600 text-[var(--background-main)] border border-black"
        : variant === "white"
          ? "bg-[var(--background-main)] text-text-primary"
          : "bg-[var(--background-main)] text-text-primary border-2 border-primary-600";
    return (
      <button ref={ref} className={cn(baseClasses, styles, className)} {...props}>
        <GoogleLogo />
        <span>{children ?? "Sign up with Google"}</span>
      </button>
    );
  },
);
GoogleSignUpButton.displayName = "GoogleSignUpButton";